import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { styled } from '../../../shared/Theme/theme';
import Button from '../../../shared/SmallComponents/Button';
import { TextInput } from '../../../shared/inputs/base';
import Loader from '../../../shared/SmallComponents/Loader';
import { localizationSelectors } from '../../../../state/ducks/localization/selectors';
import { RootState } from '../../../../state/store/store';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { filterFieldsType } from '../../../../state/ducks/allShipmentsPage/allShipmentsPage';
import ConsolidationsFiltersStripe from './filter/ConsolidationsFiltersStripe';
import ConsolidationsFilterPopover from './filter/ConsolidationsFilterPopover';
import { getConsolidationActiveFiltersCount } from './filter/consolidationsFilterUtils';

type Props = {
    localization: any;
    totalCount: number;
    isLoading?: boolean;
    filterFields: filterFieldsType;
    showInactive?: boolean;
    exportSlot?: React.ReactNode;
    onToggleActiveView?: () => void;
    onRefresh: () => void;
    onFilterChange: (text: string) => void;
    onApplyFilters: (filter: filterFieldsType) => void;
    currentPage: number;
    rowsPerPage: number;
    onPageChange: (page: number) => void;
};

const ConsolidationsPageHeader = ({
    localization,
    totalCount,
    isLoading,
    filterFields,
    showInactive,
    exportSlot,
    onToggleActiveView,
    onRefresh,
    onFilterChange,
    onApplyFilters,
    currentPage,
    rowsPerPage,
    onPageChange
}: Props) => {
    const [textFilterValue, setTextFilterValue] = useState('');
    const [isTypingTextFilter, setIsTypingTextFilter] = useState(false);

    const activeFiltersCount = getConsolidationActiveFiltersCount(filterFields);

    useEffect(() => {
        setIsTypingTextFilter(true);
        const timeout = setTimeout(() => {
            onFilterChange(textFilterValue || '');
            setIsTypingTextFilter(false);
            onPageChange(0);
        }, 500);
        return () => clearTimeout(timeout);
    }, [textFilterValue, onFilterChange, onPageChange]);

    const shipmentsFrom = totalCount === 0 ? 0 : currentPage * rowsPerPage + 1;
    const shipmentsTo = Math.min((currentPage + 1) * rowsPerPage, totalCount);

    return (
        <PageHeader>
            <Title>Consolidations</Title>
            <ConsolidationsFiltersStripe filterFields={filterFields} setFilterFields={onApplyFilters} localization={localization} />

            <HeaderRightSide>
                {onToggleActiveView && (
                    <ActiveToggleBtn
                        type="button"
                        $inactive={!!showInactive}
                        onClick={onToggleActiveView}
                    >
                        {showInactive ? 'Inactive' : 'Active'}
                    </ActiveToggleBtn>
                )}
                <Button
                    width="32px"
                    height="32px"
                    borderRadius="50%"
                    margin="7px"
                    buttonType="hollow"
                    onClick={onRefresh}
                >
                    <Tooltip title={localization?.refresh_btn || 'Refresh'} aria-label="refresh">
                        <span className="material-icons">refresh</span>
                    </Tooltip>
                </Button>
                <TextInput
                    style={{
                        height: '30px',
                        margin: '4px',
                        width: 'auto'
                    }}
                    allowClear
                    onChange={(value: string) => setTextFilterValue(value)}
                    placeHolder={localization?.filterPlaceHolder || 'Filter consolidations'}
                    value={textFilterValue || ''}
                    suffix={isTypingTextFilter && <Loader wrapperWidth="auto" showText={false} marginTop="0" width="15px" />}
                />
                <ConsolidationsFilterPopover
                    activeFiltersCount={activeFiltersCount}
                    localization={localization?.filter}
                    filterFields={filterFields}
                    onApplyFilters={onApplyFilters}
                />
                {exportSlot}
                {!isLoading && totalCount > 0 && (
                    <NavButtonsContainer>
                        <IconButton
                            onClick={() => onPageChange(currentPage - 1)}
                            disabled={isLoading || shipmentsFrom <= 1}
                            className="material-icons nav-btn"
                        >
                            navigate_before
                        </IconButton>
                        {totalCount > 0
                            ? `${shipmentsFrom} – ${shipmentsTo}${totalCount > shipmentsTo ? ` of ${totalCount}` : ''}`
                            : ''}
                        <IconButton
                            onClick={() => onPageChange(currentPage + 1)}
                            disabled={isLoading || shipmentsTo >= totalCount}
                            className="material-icons nav-btn"
                        >
                            navigate_next
                        </IconButton>
                    </NavButtonsContainer>
                )}
            </HeaderRightSide>
        </PageHeader>
    );
};

const PageHeader = styled.div`
    display: flex;
    padding: 20px 12px 0 12px;
    align-items: center;
    position: sticky;
    top: 0;
    left: 0;
    z-index: 1000;
    background-color: #fff;
    border-bottom: solid 1px ${(props) => props.theme.colors.grayDefaultBorder};

    @media screen and (max-width: 768px) {
        justify-content: center;
        padding: 20px 8px 8px 8px;
    }
`;

const Title = styled.div`
    display: flex;
    color: ${(props) => props.theme.colors.pageTitle};
    font-size: ${(props) => props.theme.fontSizes.pageTitle};
    font-weight: bold;

    @media screen and (max-width: 768px) {
        display: none;
    }
`;

const HeaderRightSide = styled.div`
    display: flex;
    width: 100%;
    max-width: fit-content;
    align-items: center;

    @media screen and (max-width: 768px) {
        height: 40px;
        justify-content: center;
    }
`;

const NavButtonsContainer = styled.div`
    display: flex;
    align-items: center;
    margin: 0 15px;
    color: ${(props) => props.theme.colors.primaryBlue};
    font-weight: bold;

    .nav-btn {
        font-size: 1.4rem;
        color: ${(props) => props.theme.colors.primaryBlue};
    }
`;

const ActiveToggleBtn = styled.button<{ $inactive?: boolean }>`
    height: 30px;
    margin: 4px 8px 4px 0;
    padding: 0 12px;
    border-radius: 16px;
    border: 1px solid ${(p) => (p.$inactive ? '#ff7676' : '#008ac9')};
    background: ${(p) => (p.$inactive ? '#fff5f5' : '#e8f4fc')};
    color: ${(p) => (p.$inactive ? '#ff7676' : '#008ac9')};
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
`;

const mapStateToProps = (state: RootState) => ({
    localization: localizationSelectors.allShipmentsPage(state)
});

export default connect(mapStateToProps)(ConsolidationsPageHeader);
